/**
 * Canonical Pairvelle community standards — published in full for maximum transparency.
 * Counsel review required before treating as binding legal text.
 */

export type RuleExample = {
  label: string
  text: string
  ok: boolean
}

export type CommunityRule = {
  id: string
  title: string
  summary: string
  rule: string
  examples: RuleExample[]
  typicalActions: string[]
}

export type RuleSection = {
  id: string
  title: string
  intro?: string
  rules: CommunityRule[]
}

export type StandardsRevision = {
  version: string
  effectiveDate: string
  summary: string
}

/** Public version string shown on Safety & Rules and API exports */
export const COMMUNITY_RULES_VERSION = '2026-07-24.1'

export const COMMUNITY_RULES_EFFECTIVE_DATE = '2026-07-22'

export const STANDARDS_CHANGELOG: StandardsRevision[] = [
  {
    version: '2026-07-22.2',
    effectiveDate: '2026-07-22',
    summary:
      'Clarified enforcement severity factors, visibility transparency, report misuse scope, fraud vs platform gifts, suspension-before-removal language, and standards retroactivity.',
  },
  {
    version: '2026-07-22',
    effectiveDate: '2026-07-22',
    summary: 'Initial published community standards.',
  },
]

export const STANDARDS_RETROACTIVITY =
  'We publish dated revisions to these standards. Conduct is evaluated under the rules in effect when it occurred, except where an immediate change is required by law or member safety.'

export const COMMUNITY_RULES_PREAMBLE = {
  headline: 'No hidden rules. No invisible penalties.',
  lead:
    'Our standards are published and apply equally to everyone. If your account or content is restricted, you will see the specific rule involved, what action was taken, how long it lasts, and how to appeal.',
  moderation:
    'We do not promise that moderation will never make a mistake. Pairvelle\'s advantage is that we explain our rules, show their enforcement, and provide recourse. No one is exiled in silence — if we limit your account, you will know.',
  equalApplication:
    'These rules, prices, and permissions apply identically to every gender. Mutual matches always message free. Daily interest limits and introduction costs apply equally to everyone.',
}

export const ENFORCEMENT_TRANSPARENCY = {
  title: 'How enforcement works',
  points: [
    'Every member has an Account Standing page showing current status, verification, and any active limitations.',
    'Every enforcement notice includes a reason category, a specific rule reference, a start time, a duration (or “indefinite — appealable”), and an appeal button.',
    'Any limitation affecting your visibility, discovery, messaging, or account access will appear in Account Standing when it takes effect. Pairvelle does not secretly reduce distribution as a disciplinary action.',
    'Temporary automated safety holds are visible to the affected member immediately — including reduced visibility, if applied.',
    'Suspension pending human review may apply before any permanent removal. Permanent suspension is reviewable and appealable. Appeal history is visible in your Account Standing.',
    'We may withhold specific evidence when sharing it would endanger another member or violate the law — but we always tell you which rule was involved and what action we took.',
  ],
}

export const ENFORCEMENT_SEVERITY = {
  title: 'How we choose enforcement severity',
  intro:
    '“Typical enforcement” describes common outcomes — not a guarantee. Actual action depends on the factors below. We apply the least restrictive measure that addresses the harm.',
  factors: [
    {
      name: 'Harm',
      detail: 'Physical safety risk, sexual exploitation, fraud losses, or severe emotional harm weigh toward stronger action.',
    },
    {
      name: 'Intent',
      detail: 'Deliberate rule-breaking, deception, or predatory behavior weighs more than an honest mistake or ambiguous edge case.',
    },
    {
      name: 'Repetition',
      detail: 'A first-time minor issue may receive a warning or short hold; repeated violations escalate.',
    },
    {
      name: 'Evidence',
      detail: 'Clear reports, message history, or photo evidence supports stronger action; ambiguous cases default to review before permanent removal.',
    },
    {
      name: 'Prior violations',
      detail: 'Active or recent enforcement notices on your account increase severity for new violations.',
    },
  ],
  note: 'Automated holds may apply instantly for safety (e.g. suspected minors). Permanent removal requires human review except where law or imminent harm requires immediate account lock.',
}

/** Wording used wherever permanent removal is possible */
export const SUSPENSION_BEFORE_REMOVAL =
  'Immediate suspension pending human review; permanent removal if confirmed'

export const APPEALS_PROCESS = {
  title: 'Appeals',
  steps: [
    'Open Settings → Account standing (or the enforcement notice you received).',
    'Read the specific rule, action taken, and duration listed on the notice.',
    'Tap Appeal and explain why you believe the action should be reviewed. Include relevant context — we cannot see tone or intent that is not in the record.',
    'Appeals are queued for human review. Status and any response appear in your appeal history.',
    'Submitting an appeal does not guarantee reversal. It guarantees review.',
  ],
  timelines:
    'Automated holds may lift automatically when their duration expires. Appeals on indefinite actions are reviewed by humans; response times vary with queue volume. We prioritize safety-related appeals when staffing permits.',
}

export const COMMUNITY_RULE_SECTIONS: RuleSection[] = [
  {
    id: 'eligibility',
    title: 'Eligibility & account integrity',
    intro: 'Pairvelle is for adults seeking genuine connections. One person, one account.',
    rules: [
      {
        id: 'age-18',
        title: 'You must be 18 or older',
        summary: 'Members must confirm they are at least 18 before using discovery or messaging.',
        rule: 'You may not create or use an account if you are under 18. You may not present yourself as under 18 on a dating platform.',
        examples: [
          { label: 'Allowed', text: 'You are 22, your profile says 22, and you confirmed 18+ at signup.', ok: true },
          { label: 'Not allowed', text: 'You are 17, or you list an age under 18 anywhere on your profile.', ok: false },
          { label: 'Not allowed', text: 'You ask someone who appears under 18 to continue using the app.', ok: false },
        ],
        typicalActions: [
          'Immediate suspension pending human review',
          'Permanent removal if under-18 use is confirmed',
          'Report forwarded to authorities when legally required',
        ],
      },
      {
        id: 'one-account',
        title: 'One account per person',
        summary: 'Duplicate, ban-evasion, and shared accounts undermine trust.',
        rule: 'You may not operate multiple accounts, share an account, or create a new account to evade enforcement.',
        examples: [
          { label: 'Allowed', text: 'You deleted your old account and created a new one after a long break, with no active ban.', ok: true },
          { label: 'Not allowed', text: 'You make a second account after a suspension to keep messaging people.', ok: false },
          { label: 'Not allowed', text: 'You and a friend share one login to swipe for each other.', ok: false },
        ],
        typicalActions: [
          'Suspension of all related accounts pending review',
          'Permanent removal if ban evasion is confirmed',
        ],
      },
      {
        id: 'authentic-identity',
        title: 'Be yourself — no impersonation',
        summary: 'Profiles must represent the person behind the account.',
        rule: 'You may not impersonate another person, use someone else\'s photos without permission, or misrepresent your identity in a way that could mislead others.',
        examples: [
          { label: 'Allowed', text: 'Your photos are of you, recent, and your display name is a nickname you actually use.', ok: true },
          { label: 'Not allowed', text: 'You use a celebrity\'s photos and pretend to be them.', ok: false },
          { label: 'Not allowed', text: 'You use your friend\'s photos without their consent.', ok: false },
        ],
        typicalActions: ['Photo removal', 'Profile edit required', 'Suspension for repeated or severe impersonation'],
      },
    ],
  },
  {
    id: 'respect',
    title: 'Respect & safety',
    intro: 'Treat other members as people, not targets.',
    rules: [
      {
        id: 'harassment',
        title: 'No harassment, threats, or hate',
        summary: 'Abusive behavior toward members or groups is not tolerated.',
        rule: 'You may not harass, threaten, stalk, or direct hate toward anyone based on protected characteristics or any other basis.',
        examples: [
          { label: 'Allowed', text: 'You decline a match politely: “Not what I’m looking for — best of luck.”', ok: true },
          { label: 'Not allowed', text: 'After a rejection you send insults, slurs, or repeated unwanted messages.', ok: false },
          { label: 'Not allowed', text: 'You threaten to harm someone or publish their private information.', ok: false },
        ],
        typicalActions: [
          'Messaging restriction',
          'Temporary suspension',
          'Suspension pending review; permanent removal if confirmed for severe cases',
        ],
      },
      {
        id: 'unwanted-contact',
        title: 'Respect boundaries',
        summary: 'No means no — including blocks and ignored messages.',
        rule: 'You may not continue contacting someone who blocked you, declined you, or clearly asked you to stop.',
        examples: [
          { label: 'Allowed', text: 'Someone passes on you; you move on.', ok: true },
          { label: 'Not allowed', text: 'Someone blocks you; you create a new account to contact them.', ok: false },
          { label: 'Not allowed', text: 'You copy-paste the same introduction to hundreds of people despite low reply rates.', ok: false },
        ],
        typicalActions: ['Introduction limit', 'Temporary messaging hold', 'Suspension'],
      },
      {
        id: 'minors',
        title: 'Zero tolerance for minors',
        summary: 'Any sexual or exploitative contact involving minors is prohibited and reported.',
        rule: 'You may not engage in sexual content, grooming, or exploitation involving anyone under 18. Report suspected minors immediately.',
        examples: [
          { label: 'Required', text: 'You report a profile that appears to be a minor.', ok: true },
          { label: 'Not allowed', text: 'Any sexual conversation where a participant is or appears to be under 18.', ok: false },
        ],
        typicalActions: [
          SUSPENSION_BEFORE_REMOVAL,
          'Preservation of evidence for authorities when legally required',
        ],
      },
    ],
  },
  {
    id: 'content',
    title: 'Photos, profiles & messages',
    intro: 'Keep public-facing content appropriate for a dating app.',
    rules: [
      {
        id: 'photo-standards',
        title: 'Profile photos',
        summary: 'Photos must be yours, clear enough to recognize you, and safe for a public dating context.',
        rule: 'You may not upload illegal content, explicit nudity, graphic violence against others, hateful imagery, or photos of people who have not consented to appear on your profile. Scars, bruises, and wounds on your own body are allowed when your face is visible and your account is verified.',
        examples: [
          { label: 'Allowed', text: 'A clear face photo, a full-body photo at the beach, a hobby photo.', ok: true },
          { label: 'Allowed', text: 'A photo of your own injury, scar, or bloody wound with your face visible (verified accounts).', ok: true },
          { label: 'Not allowed', text: 'Explicit nudity or sexual acts in a public profile photo.', ok: false },
          { label: 'Not allowed', text: 'Corpses, extreme gore of others, or imagery that promotes self-harm.', ok: false },
          { label: 'Not allowed', text: 'A group photo where you cannot tell who the profile owner is, used as your only photo.', ok: false },
        ],
        typicalActions: [
          'Photo removed pending review',
          'Profile hidden until compliant photos uploaded (visible in Account Standing)',
        ],
      },
      {
        id: 'message-content',
        title: 'Message content',
        summary: 'The same standards apply in chat.',
        rule: 'You may not send illegal content, non-consensual intimate imagery, malware links, or messages primarily intended to scam or harm.',
        examples: [
          { label: 'Allowed', text: 'Getting to know someone, making plans to meet in public, sharing hobbies.', ok: true },
          { label: 'Not allowed', text: 'Sending unsolicited explicit images.', ok: false },
          { label: 'Not allowed', text: 'Phishing links or off-platform money demands.', ok: false },
        ],
        typicalActions: ['Message removal', 'Messaging restriction', 'Suspension'],
      },
      {
        id: 'bio-accuracy',
        title: 'Honest profiles',
        summary: 'Do not deceive others about who you are or what you want.',
        rule: 'Your bio, age, location, and stated preferences must be substantially accurate. Optional lifestyle tags describe you — they are never inferred from gender.',
        examples: [
          { label: 'Allowed', text: 'You list “Casual” as connection intent and mean it.', ok: true },
          { label: 'Not allowed', text: 'You list a false age or city to appear in searches you would not otherwise match.', ok: false },
        ],
        typicalActions: [
          'Edit required',
          'Reduced visibility until corrected (listed in Account Standing when applied)',
          'Suspension for fraud',
        ],
      },
    ],
  },
  {
    id: 'platform',
    title: 'Platform use & fairness',
    intro: 'Product rules exist to keep attention meaningful and inboxes manageable.',
    rules: [
      {
        id: 'daily-likes',
        title: 'Daily interest limits',
        summary: 'Everyone has a daily cap on expressions of interest to prevent inbox floods.',
        rule: 'You may not automate, script, or buy your way around daily like limits. The limit applies equally to every gender.',
        examples: [
          { label: 'Allowed', text: 'You use your daily likes thoughtfully on people who fit your preferences.', ok: true },
          { label: 'Not allowed', text: 'You use bots or third-party tools to mass-like profiles.', ok: false },
        ],
        typicalActions: ['Temporary introduction block', 'Account review', 'Suspension for automation'],
      },
      {
        id: 'messaging-economics',
        title: 'Messaging rules',
        summary: 'Mutual matches message free. Unmatched introductions may cost credits — same price for everyone.',
        rule: 'Mutual matches always message free with no per-message fee. Unmatched introductions go to the Requests inbox; the first message may cost credits, shown before you send. Recipients always reply free.',
        examples: [
          { label: 'Free', text: 'You and someone else liked each other — chat in Matches at no charge.', ok: true },
          { label: 'Paid intro', text: 'You send an introduction to someone you have not matched with — credits are shown and confirmed before send.', ok: true },
          { label: 'Not allowed', text: 'Attempting to bypass payment or rate limits via exploits or modified clients.', ok: false },
        ],
        typicalActions: ['Transaction reversed', 'Introduction block', 'Suspension'],
      },
      {
        id: 'spam-commercial',
        title: 'No spam or unsolicited promotion',
        summary: 'PairVelle is for connection — not inbox spam.',
        rule: 'You may not use PairVelle primarily to send copy-pasted promotional messages, recruit for multi-level marketing, or drive traffic through unsolicited DMs. Approved social links on your profile (Instagram, TikTok, creator platforms, etc.) are allowed; blasting those links in messages is not.',
        examples: [
          { label: 'Allowed', text: 'Instagram and TikTok linked on your profile; mentioning your stream when someone asks.', ok: true },
          { label: 'Not allowed', text: 'Copy-pasted messages asking every match to subscribe to an external channel.', ok: false },
          { label: 'Not allowed', text: 'Mass identical introductions promoting a business or random websites.', ok: false },
        ],
        typicalActions: [
          'Message removal',
          'Introduction restriction',
          'Suspension pending review; permanent removal if confirmed for commercial spam networks',
        ],
      },
      {
        id: 'fraud',
        title: 'No fraud or scams',
        summary: 'Romance scams hurt real people. We treat them seriously.',
        rule:
          'You may not solicit money, gift cards, crypto, wire transfers, or financial information from other members through Pairvelle messages or profiles, nor run investment or emergency-scam schemes. If Pairvelle offers approved in-app gifts or credits in the future, those flows are the only permitted way to send money through the platform — asking members to pay you off-platform remains prohibited.',
        examples: [
          { label: 'Not allowed', text: '“I’m stranded abroad — wire me money.”', ok: false },
          { label: 'Not allowed', text: 'Asking a new match to invest in a “sure thing.”', ok: false },
          { label: 'Allowed (future)', text: 'Sending an optional, disclosed in-app gift through Pairvelle’s own checkout — when and if that feature is offered.', ok: true },
        ],
        typicalActions: [
          SUSPENSION_BEFORE_REMOVAL,
          'Permanent removal if fraud is confirmed',
          'Reports preserved for law enforcement when appropriate',
        ],
      },
    ],
  },
  {
    id: 'your-tools',
    title: 'Your safety tools',
    intro: 'You control who can reach you.',
    rules: [
      {
        id: 'block-report',
        title: 'Block and report',
        summary: 'Available on every profile and conversation.',
        rule:
          'You may block anyone at any time for any reason. You should report content or behavior that you believe violates these standards — honest reports that are not substantiated are not misuse. Enforcement applies to knowingly false, retaliatory, or coordinated reports.',
        examples: [
          { label: 'Good use', text: 'You report someone who sent threatening messages.', ok: true },
          { label: 'Good use', text: 'You report someone who made you uncomfortable; moderators review and may not find a violation — that is OK.', ok: true },
          { label: 'Misuse', text: 'You mass-report people who simply declined you, or coordinate false reports with friends.', ok: false },
        ],
        typicalActions: ['Block is immediate and private', 'Reports queued for moderation review'],
      },
      {
        id: 'filters',
        title: 'Discovery filters',
        summary: 'Recipients control who can introduce themselves.',
        rule: 'You may filter who can send you unmatched introductions — including verified-only filters. Others must respect your outreach preference: initiate, be approached, or either.',
        examples: [
          { label: 'Allowed', text: 'You set verified-only introductions because you prefer it.', ok: true },
          { label: 'Not allowed', text: 'Harassing someone because they will not change their filters for you.', ok: false },
        ],
        typicalActions: ['N/A — these are member controls', 'Harassment enforced under Respect rules'],
      },
    ],
  },
]

/** Plain-text export for API / SEO rendering */
export function communityRulesPlainText(): string {
  const lines: string[] = [
    COMMUNITY_RULES_PREAMBLE.headline,
    '',
    COMMUNITY_RULES_PREAMBLE.lead,
    '',
    COMMUNITY_RULES_PREAMBLE.moderation,
    '',
    `Version: ${COMMUNITY_RULES_VERSION}`,
    `Effective date: ${COMMUNITY_RULES_EFFECTIVE_DATE}`,
    '',
    'STANDARDS REVISIONS',
    STANDARDS_RETROACTIVITY,
    ...STANDARDS_CHANGELOG.map((r) => `• ${r.version} (${r.effectiveDate}): ${r.summary}`),
    '',
    ENFORCEMENT_TRANSPARENCY.title,
    ...ENFORCEMENT_TRANSPARENCY.points.map((p) => `• ${p}`),
    '',
    ENFORCEMENT_SEVERITY.title,
    ENFORCEMENT_SEVERITY.intro,
    ...ENFORCEMENT_SEVERITY.factors.map((f) => `• ${f.name}: ${f.detail}`),
    ENFORCEMENT_SEVERITY.note,
    '',
  ]
  for (const section of COMMUNITY_RULE_SECTIONS) {
    lines.push(section.title.toUpperCase())
    if (section.intro) lines.push(section.intro)
    lines.push('')
    for (const rule of section.rules) {
      lines.push(`${rule.id} — ${rule.title}`)
      lines.push(rule.rule)
      lines.push('Examples:')
      for (const ex of rule.examples) {
        lines.push(`  ${ex.ok ? '✓' : '✗'} ${ex.label}: ${ex.text}`)
      }
      lines.push(`Typical actions: ${rule.typicalActions.join('; ')}`)
      lines.push('')
    }
  }
  lines.push(APPEALS_PROCESS.title)
  for (const step of APPEALS_PROCESS.steps) {
    lines.push(`• ${step}`)
  }
  lines.push('')
  lines.push(APPEALS_PROCESS.timelines)
  return lines.join('\n')
}
